/**
 * Conversations tab inside the project view.
 *
 * Lists the project's conversations (filtered by projectId on the
 * backend via ?projectId=) and offers two entry points:
 *   - "+ New conversation" — opens PersonaPickerModal for single-persona chat
 *   - "+ New roundtable"  — opens PanelPicker for 2–3 persona debate (§7h)
 *
 * Clicking a conversation row navigates into the project-scoped
 * chat or roundtable URL depending on the conversation's type.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "../components/EmptyState.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import PersonaPickerModal from "../components/PersonaPickerModal.jsx";
import PanelPicker from "../components/PanelPicker.jsx";
import { useStore } from "../store/useStore.js";

const dateFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export default function ProjectConversationsTab() {
  const { projectId } = useParams();
  const conversations = useStore((s) => s.conversations);
  const loading = useStore((s) => s.conversationsLoading);
  const loadError = useStore((s) => s.conversationsError);
  const loadConversations = useStore((s) => s.loadConversations);
  const removeConversation = useStore((s) => s.removeConversation);
  const pushToast = useStore((s) => s.pushToast);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [panelPickerOpen, setPanelPickerOpen] = useState(false);
  // Delete-action error is kept local so it doesn't survive route changes.
  // Load errors come from the store so the conversation list stays
  // consistent across re-mounts.
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    loadConversations(projectId);
  }, [loadConversations, projectId]);

  const handleDelete = async (e, id, title) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Delete "${title || "Untitled conversation"}"?`)) return;
    try {
      await removeConversation(id);
      pushToast("Conversation deleted", "info");
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  return (
    <div className="project-conversations">
      <div className="project-tab-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setPickerOpen(true)}
        >
          + New conversation
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setPanelPickerOpen(true)}
        >
          + New roundtable
        </button>
      </div>

      {(loadError || deleteError) && (
        <ErrorBubble message={loadError || deleteError} />
      )}

      {loading && conversations.length === 0 ? (
        <div className="conversation-list-loading">Loading…</div>
      ) : conversations.length === 0 ? (
        <EmptyState
          title="No conversations yet"
          description="Start a new coaching conversation to work through a question or frame a decision."
          action={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setPickerOpen(true)}
            >
              Start a conversation
            </button>
          }
        />
      ) : (
        <ul className="conversation-list">
          {conversations.map((c) => {
            const isRoundtable = c.type === "roundtable";
            const href = isRoundtable
              ? `/projects/${projectId}/roundtable/${c.id}`
              : `/projects/${projectId}/chat/${c.personaId}/${c.id}`;
            // Roundtable rows show "RT" badge + comma-joined panellist ids;
            // single rows show just the persona id.
            const subtitle = isRoundtable
              ? (c.personaIds || []).join(" · ")
              : c.personaId;
            return (
              <li key={c.id} className="conversation-list-item">
                <Link to={href} className="conversation-list-link">
                  <div className="conversation-list-title">
                    {isRoundtable && (
                      <span
                        className="conversation-badge"
                        aria-label="Roundtable"
                      >
                        RT
                      </span>
                    )}
                    {c.title || "Untitled conversation"}
                  </div>
                  <div className="conversation-list-meta">
                    <span>{subtitle}</span>
                    {c.updatedAt && (
                      <span>
                        {dateFormat.format(new Date(c.updatedAt))}
                      </span>
                    )}
                  </div>
                </Link>
                <button
                  type="button"
                  className="conversation-list-delete"
                  onClick={(e) => handleDelete(e, c.id, c.title)}
                  aria-label="Delete conversation"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <PersonaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        projectId={projectId}
      />
      <PanelPicker
        open={panelPickerOpen}
        onClose={() => setPanelPickerOpen(false)}
        projectId={projectId}
      />
    </div>
  );
}
